/** Recheck gate results: always printed before any narrative (audit 10.7). */
import { esc } from '../../../utils/reportCommon';
import { NA, NOT_RUN, humanKey, isNum, isRec, present } from '../primitives';
import type { CaseReportPayload } from '../types';

export type GateStatus = 'passed' | 'failed' | 'not_tested' | 'unknown';
export interface GateRow { name: string; status: GateStatus; failures: string[]; failureCount: number; warnings: string[]; checked?: number; note?: string }

const issueText = (x: unknown): string => {
  if (typeof x === 'string') return x;
  if (isRec(x)) {
    const msg = typeof x.message === 'string' ? x.message : JSON.stringify(x);
    const refs = [x.tx_hash, x.ref, x.refs].filter(present).map((r) => (Array.isArray(r) ? r.join(', ') : String(r)));
    return refs.length ? `${msg} (${refs.join('; ')})` : msg;
  }
  return JSON.stringify(x);
};

/**
 * Normalise the recheck-gate result ({status, passed_all, gates{...}|[...], summary[]}).
 * `status` wins over `passed`: a gate with nothing to check reports
 * passed=true / checked=0 and must print NOT TESTED, never PASS (R10).
 */
export function normalizeGates(g: unknown): { overall: GateStatus; rows: GateRow[]; summary: string[] } | null {
  if (!isRec(g)) return null;
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(issueText) : present(v) ? [issueText(v)] : []);
  const statusOf = (r: unknown): GateStatus => {
    if (typeof r === 'boolean') return r ? 'passed' : 'failed';
    if (!isRec(r)) return 'unknown';
    if (typeof r.status === 'string') {
      const st = r.status.toLowerCase();
      if (['pass', 'passed', 'ok', 'clear'].includes(st)) return 'passed';
      if (['fail', 'failed', 'violation'].includes(st)) return 'failed';
      if (['not_tested', 'not tested', 'skipped', 'not_run'].includes(st)) return 'not_tested';
    }
    if (isNum(r.checked) && r.checked === 0 && !list(r.failures).length) return 'not_tested';
    const b = typeof r.passed === 'boolean' ? r.passed : (typeof r.ok === 'boolean' ? r.ok : null);
    return b === null ? 'unknown' : (b ? 'passed' : 'failed');
  };
  const rowOf = (name: string, r: unknown): GateRow => {
    const rec = isRec(r) ? r : {};
    const failures = list(rec.failures ?? rec.violations ?? rec.errors ?? rec.details);
    return {
      name, status: statusOf(r), failures,
      failureCount: isNum(rec.failure_count) ? rec.failure_count : failures.length,
      warnings: list(rec.warnings),
      checked: isNum(rec.checked) ? rec.checked : undefined,
      note: typeof rec.message === 'string' ? rec.message : undefined,
    };
  };
  const rows: GateRow[] = [];
  const gates = g.gates ?? g.results;
  if (isRec(gates)) {
    for (const [name, r] of Object.entries(gates)) rows.push(rowOf(name, r));
  } else if (Array.isArray(gates)) {
    gates.forEach((r, i) => {
      const rec = isRec(r) ? r : {};
      rows.push(rowOf(String(rec.name ?? rec.id ?? rec.gate ?? rec.check ?? `gate ${i + 1}`), r));
    });
  }
  const summary = Array.isArray(g.summary) ? g.summary.map(issueText) : [];
  let overall: GateStatus = statusOf({ status: g.status });
  if (overall === 'unknown') {
    if (rows.some((r) => r.status === 'failed')) overall = 'failed';
    else if (g.passed_all === true) overall = 'passed';
    else if (rows.length && rows.every((r) => r.status === 'not_tested')) overall = 'not_tested';
  }
  return { overall, rows, summary };
}

const GATE_LABEL: Record<GateStatus, string> = {
  passed: '<span class="gate pass">PASS</span>',
  failed: '<span class="gate fail">FAIL</span>',
  not_tested: '<span class="gate unknown">NOT TESTED</span>',
  unknown: '<span class="gate unknown">not available</span>',
};

export function gatesSection(d: CaseReportPayload): string {
  const g = normalizeGates(d.recheck_gates);
  if (!g) return NOT_RUN('recheck gate results') + '<p class="muted">Gate status: not run. No narrative in this report relies on unchecked flows.</p>';
  const banner = g.overall === 'passed'
    ? '<div class="gate-banner pass">All recheck gates that had something to check passed.</div>'
    : g.overall === 'failed'
      ? '<div class="gate-banner fail">One or more recheck gates FAILED. Failures are listed below and must be resolved or explained before any finding is relied on.</div>'
      : g.overall === 'not_tested'
        ? '<div class="gate-banner unknown">Recheck gates not tested: the run had nothing for them to check.</div>'
        : '<div class="gate-banner unknown">Overall gate result: not available.</div>';
  const rows = g.rows.map((r) => {
    const shown = r.failures.slice(0, 25);
    const more = Math.max(0, r.failureCount - shown.length);
    const fails = shown.length
      ? `<ul>${shown.map((f) => `<li>${esc(f)}</li>`).join('')}${more ? `<li>… ${more} more</li>` : ''}</ul>`
      : (r.status === 'failed' ? NA : 'none');
    const warn = r.warnings.length ? `<div class="muted">Warnings: ${esc(r.warnings.slice(0, 5).join(' | '))}${r.warnings.length > 5 ? ' …' : ''}</div>` : '';
    return `<tr><td>${esc(humanKey(r.name))}</td><td>${GATE_LABEL[r.status]}</td><td>${isNum(r.checked) ? r.checked : NA}</td><td>${fails}${warn}${r.note ? `<div class="muted">${esc(r.note)}</div>` : ''}</td></tr>`;
  }).join('');
  return `${banner}
    <table class="data"><thead><tr><th>Gate</th><th>Result</th><th>Checks</th><th>Failures</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="4">No individual gate results in the payload.</td></tr>'}</tbody></table>
    ${g.summary.length ? `<ul class="gate-summary">${g.summary.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}`;
}
